import { CircleDashed, CircleX, Clock3, MessageSquareText, PenLine } from "lucide-react";
import type { ReactNode } from "react";
import { handleLocalNavigation } from "../lib/navigation";
import { compactCount, repositoryShortName, type PullException, type PullGlyph, type PullView } from "../lib/pull-view";

/** lucide dropped brand marks, and a GitHub link is only obvious with GitHub's own. */
export function GitHubMark() {
  return (
    <svg aria-hidden="true" fill="currentColor" role="img" viewBox="0 0 16 16">
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
    </svg>
  );
}

export interface PullBadgeFacts {
  repository: string;
  pullNumber: number;
  author?: string;
  additions?: number | null;
  deletions?: number | null;
  /** Used when only a combined line count is known, as on authored pull requests. */
  changedLoc?: number | null;
  changedFiles?: number | null;
}

/**
 * Repository, number, author and size — the facts that identify a pull request
 * and price it. Every surface that names a PR shows this unchanged, so the same
 * glance answers "which one is this" wherever you are. The organisation prefix
 * is always dropped: every CHEW repository lives in the same org.
 */
export function PullBadge({ facts, size = "sm" }: { facts: PullBadgeFacts; size?: "sm" | "md" }) {
  const { additions, deletions, changedLoc, changedFiles } = facts;
  const split = additions != null || deletions != null;
  const files = changedFiles != null ? ` across ${changedFiles} ${changedFiles === 1 ? "file" : "files"}` : "";
  const loc = split
    ? { text: `+${compactCount(additions ?? 0)} −${compactCount(deletions ?? 0)}`, title: `${additions ?? 0} added, ${deletions ?? 0} removed${files}` }
    : changedLoc != null
      ? { text: `${compactCount(changedLoc)} lines`, title: `${changedLoc} changed lines${files}` }
      : undefined;
  return (
    <span className="pv-badge-line">
      <span className={`pv-badge is-${size}`}>
        <span className="pv-badge-repo" title={facts.repository}>{repositoryShortName(facts.repository)}</span>
        <span className="pv-badge-number">#{facts.pullNumber}</span>
        {loc && <span className="pv-badge-loc" title={loc.title}>{loc.text}</span>}
      </span>
      {facts.author && <span className="pv-badge-author">@{facts.author}</span>}
    </span>
  );
}

const glyphs: Record<PullGlyph, typeof CircleX> = {
  fail: CircleX,
  running: CircleDashed,
  comment: MessageSquareText,
  pen: PenLine,
  clock: Clock3,
};

/**
 * Flags are glyph plus count in one conjoined group, never prose: at list
 * density the words repeat down the column and stop being read. The sentence
 * stays in the tooltip and in the accessible name.
 */
export function PullFlags({ exceptions, verbose = false }: { exceptions: PullException[]; verbose?: boolean }) {
  if (exceptions.length === 0) return null;
  return (
    <span className={`pv-flags${verbose ? " is-verbose" : ""}`}>
      {exceptions.map((exception) => {
        const Glyph = glyphs[exception.glyph];
        return (
          <span className={`pv-flag tone-${exception.tone}`} key={exception.key} title={exception.label}>
            <Glyph aria-hidden="true" />
            {verbose ? exception.label : exception.count != null && <b>{exception.count}</b>}
            <span className="pv-reader">{exception.label}</span>
          </span>
        );
      })}
    </span>
  );
}

export function PullMeasures({ view }: { view: PullView }) {
  return (
    <>
      {view.estimate && <span className="pv-measure" title={view.estimate.title}>{view.estimate.label}</span>}
      {view.age && <span className={`pv-measure pv-age tone-${view.age.tone}`} title={view.age.title}>{view.age.label}</span>}
    </>
  );
}

/**
 * One pull request card, split down the middle: what the change is on the left,
 * what its review is doing on the right. Colour is reserved for the left rule
 * and for flags, so a card with neither is a clean, unclaimed pull request.
 */
export function PullCard({
  view,
  rank,
  status,
  actions,
  trailing,
  selected = false,
  onOpen,
}: {
  view: PullView;
  rank?: number;
  /** Overrides the derived status word, for surfaces whose band already says it. */
  status?: ReactNode;
  actions?: ReactNode;
  trailing?: ReactNode;
  selected?: boolean;
  onOpen?: () => void;
}) {
  const path = view.identity.reviewPath;
  return (
    <article className={`pv-card channel-${view.channel}${selected ? " is-selected" : ""}`}>
      {rank !== undefined && <span className="pv-rank">{rank}</span>}
      <a
        className="pv-card-id"
        href={path}
        onClick={(event) => {
          if (onOpen && !event.metaKey && !event.ctrlKey) { event.preventDefault(); onOpen(); return; }
          handleLocalNavigation(event, path);
        }}
      >
        <span className="pv-card-title">{view.identity.title}</span>
        <PullBadge facts={view.badge} />
      </a>
      <div className="pv-card-review">
        <PullFlags exceptions={view.exceptions} />
        {view.holder && (
          <span className={`pv-holder${view.holder.isViewer ? " is-you" : ""}`} title={view.holder.detail}>
            {view.holder.isViewer ? "yours" : `@${view.holder.label}`}
          </span>
        )}
        {status ?? <span className="pv-status" title={view.state.detail}>{view.state.label}</span>}
        {trailing}
        <PullMeasures view={view} />
        <a
          aria-label={`Open ${repositoryShortName(view.badge.repository)} #${view.badge.pullNumber} on GitHub`}
          className="pv-github"
          href={view.identity.url}
          onClick={(event) => event.stopPropagation()}
          rel="noreferrer"
          target="_blank"
          title="Open on GitHub"
        ><GitHubMark /></a>
        {actions}
      </div>
    </article>
  );
}

/** Bands group cards by what a reviewer can do about them, so no filter is needed to find out. */
export function PullBand({
  title,
  blurb,
  count,
  children,
}: {
  title: string;
  blurb: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <section className="pv-band">
      <header>
        <h3>{title}</h3>
        <span className="pv-band-count">{count}</span>
        <p>{blurb}</p>
      </header>
      <div className="pv-band-cards">{children}</div>
    </section>
  );
}
